import { Component, OnInit, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

// services
import { EventsService } from '../../core/services/events-service/events.service';
import { ProfileService } from '../../core/services/profile-service/profile.service';

// components
import { AlertComponent } from '../../shared';

// constants
import {
	tshirtSizes,
	CalculateAge,
	_calculateAge,
	ScrollIt,
	_scrollIt,
	scrollItType,
	getPhoneMask,
	stateList,
} from '../../shared';

// interfaces
import { School } from '../interfaces';
import { PrefillFormComponent } from '../prefill-form/prefill-form.component';

const refRoles = [
	'Corner Judge',
	'Center Referee',
	'Sport Poomsae Judge',
	'TechnicalAssistant',
];

const refCertForms = [
	'Non Certified',
	'AAU Certified',
	'USAT Level 1',
	'USAT Level 2',
	'WTF Poomsae IR',
	'CUTA Certified',
];

const refCertSparring = [
	'Non Certified',
	'AAU Certified',
	'CUTA Certified',
	'USAT D2',
	'USAT D1',
	'USAT C2',
	'USAT C1',
	'USAT B2',
	'USAT B1',
	'USAT A2',
	'USAT A1',
	'WTF IR (Kyorugi 3rd Class)',
	'WTF IR (Kyorugi 2nd Class)',
	'WTF IR (Kyorugi 1st Class)',
	'WTF IR (Kyorugi S Class)',
];

@Component({
	selector: 'eze-referee-registration',
	templateUrl: './referee-registration.component.html',
	styleUrls: ['./referee-registration.component.css'],
})
export class RefereeRegistrationComponent implements OnInit {
	form: FormGroup;
	showValidation = false;
	eventId = '';
	successHtml = '';
	hasSelectedPrefill = false;

	@ViewChild(AlertComponent, { static: false })
	alert: AlertComponent;
	@ViewChild('waiverModal', { static: false })
	waiverModal;
	@ViewChild('termsModal', { static: false })
	termsModal;

	@ViewChild(PrefillFormComponent, { static: false })
	prefillFormComponent: PrefillFormComponent;

	get shirtSizes(): string[] {
		return tshirtSizes;
	}

	get refRoles(): string[] {
		return refRoles;
	}
	get refCertForms(): string[] {
		return refCertForms;
	}
	get refCertSparring(): string[] {
		return refCertSparring;
	}

	get waiverText(): string {
		return this.eventService.waiver.text;
	}

	get waiverHeader(): string {
		return this.eventService.waiver.header;
	}

	get isClosed(): boolean {
		return this.eventService.isRegistrationClosed('referee');
	}

	get eventUrl(): string {
		return this.eventService.eventData.url;
	}

	get stateList(): string[] {
		return stateList;
	}

	get profile() {
		return this.profileService.profile;
	}

	get isGuest() {
		return this.profileService.isGuest;
	}

	get schools(): School[] {
		return this.eventService.schools;
	}

	get membershipIdField():
		| {
				required: boolean;
				label: string;
		  }
		| undefined {
		return this.eventService.eventData.registrations.referees.membership_id;
	}

	get roleRequired(): boolean {
		return this.eventService.eventData.registrations.referees.role;
	}

	get message(): string | undefined {
		return this.eventService.eventData.registrations.referees.message;
	}

	getExtraFields(): { [key: string]: any } {
		const output: Record<string, any[]> = {};

		if (this.membershipIdField) {
			output.membership_id = [
				'',
				this.membershipIdField.required ? Validators.required : [],
			];
		}

		if (this.roleRequired) {
			output.role = ['', Validators.required];
		}

		return output;
	}

	constructor(
		private fb: FormBuilder,
		@Inject(CalculateAge) private calculateAge,
		@Inject(ScrollIt) private scrollIt: scrollItType,
		private eventService: EventsService,
		private route: ActivatedRoute,
		private profileService: ProfileService
	) {}

	ngOnInit() {
		this.initializeForm();
		this.getEventId();
	}

	getEventId() {
		this.eventId = this.route.snapshot.parent.params.id;
	}

	shirtSizeRequired() {
		if (this.eventService.eventData.registrations.referees.shirt) {
			return { shirt_size: ['', Validators.required] };
		} else {
			return {};
		}
	}

	hotelRequired() {
		if (this.eventService.eventData.registrations.referees.hotel) {
			return { hotel: [false] };
		} else {
			return {};
		}
	}

	initializeForm() {
		this.form = this.fb.group(
			{
				// personal information
				name_first: ['', Validators.required],
				name_middle: [''],
				name_last: ['', Validators.required],

				birth_date: ['', Validators.required],

				gender: ['', Validators.required],

				// contact information
				email: ['', [Validators.required, Validators.email]],
				phone: ['', Validators.required],
				address: ['', Validators.required],
				address_2: [''],
				city: ['', Validators.required],
				state: ['', Validators.required],
				zip: ['', Validators.required],

				// misc information
				...this.shirtSizeRequired(),

				school: this.fb.group(
					{
						id: [''],
						name: [''],
						master: [''],
						address: [''],
						address_2: [''],
						city: [''],
						state: [''],
						zip: [''],
						country: [''],
						phone: [''],
						email: [''],
						url: [''],
					},
					{
						validator: this.schoolRequired,
					}
				),
				cert_forms: ['', Validators.required],
				cert_sparring: ['', Validators.required],

				...this.getExtraFields(),

				// parent information
				parent_name_first: [''],
				parent_name_middle: [''],
				parent_name_last: [''],
				parent_email: [''],
				parent_phone: [''],

				// legal stuff and subsrcriptions
				...this.hotelRequired(),

				subscription: [false],
				legal_age: [false, Validators.requiredTrue],
				no_crime: [false, Validators.requiredTrue],
				waiver: [false, Validators.requiredTrue],
				terms_and_conditions: [false, Validators.requiredTrue],
				catchall: [false, Validators.requiredTrue],
			},
			{
				validator: (control) => this.parentRequired(control),
			}
		);

		// console.log('adding mock data');
		// this.form.patchValue(mockData);
	}

	schoolRequired(control: FormGroup) {
		const value = control.value;
		if (value.id === '') {
			return { required: true };
		}

		if (value.id !== '-1') {
			return null;
		}

		const schoolFields = [
			'id',
			'name',
			'master',
			'address',
			'city',
			'state',
			'zip',
			'phone',
		];

		const output = {};

		schoolFields.forEach((field) => {
			if (value[field] === '') {
				output[field] = { required: true };
			}
		});

		if (Object.keys(output).length > 0) {
			return output;
		} else {
			return null;
		}
	}

	parentRequired(control: FormGroup) {
		const value = control.value;
		if (!this.form || !this.isMinor(this.form)) {
			return null;
		}

		const parentFields = [
			'parent_name_first',
			'parent_name_last',
			'parent_email',
			'parent_phone',
		];

		const output = {};

		parentFields.forEach((field) => {
			if (value[field] === '') {
				output[field] = { required: true };
			}
		});

		if (Object.keys(output).length > 0) {
			return output;
		} else {
			return null;
		}
	}

	isMinor(formGroup: FormGroup): boolean {
		const birth_date = formGroup.get('birth_date').value;
		return this.calculateAge(new Date(birth_date)) < 18;
	}

	onSubmit() {
		this.scrollIt(0);
		if (this.form.invalid) {
			this.alert.newAlert('Please fill out required fields', 'danger');
			this.showValidation = true;
			return;
		}

		this.alert.newAlert('Submitting...', 'info');
		this.eventService.postReferee(this.eventId, this.form.value).subscribe(
			(res: any) => {
				if (res && res._html_message) {
					this.successHtml = res._html_message;
				} else {
					this.alert.newAlert('There was an error', 'danger');
				}
				// TODO - add type to res
				// this.alert.newAlert('Success', 'Form has been submitted!', 'success');
			},
			(err) => {
				// TODO - handle?
				this.alert.newAlert('There was an error', 'danger');
			}
		);
	}

	resetForm() {
		this.form.reset();
		this.successHtml = '';
		this.showValidation = false;
		this.hasSelectedPrefill = false;
	}

	openModal(event: Event, modalName: string) {
		event.preventDefault();
		switch (modalName) {
			case 'waiver':
				this.waiverModal.open();
				break;
			case 'terms':
				this.termsModal.open();
				break;
			default:
				break;
		}
	}

	getPhoneMask(formControl) {
		return getPhoneMask(this.form.get(formControl).value);
	}
}
